import React from "react";
import * as ATCAConstants from '../util/ATCAConstants';
import * as Utils from '../util/Utils'

import { Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Box, Typography, Snackbar, Alert} from "@mui/material";
import { Stack } from "@mui/system";
import { get, post } from 'aws-amplify/api';
import { useDropzone } from 'react-dropzone';

export default function ValidateScheduleDialog(
  props: {
    open: boolean,
    handleCancel: () => void,
  }
) {
  const [scheduleContent, setScheduleContent] = React.useState('');
  const [filename, setFilename] = React.useState('');
  const [validation, setValidation] = React.useState<string[]>([]);
  const [snackMessage, setSnackMessage] = React.useState<any>(null);
  const [correlatorSettings, setCorrelatorSettings] = React.useState<any[]>([]);

  const { getRootProps, getInputProps, open } = useDropzone
    ({maxFiles: 1, 
      multiple: false,
      accept: { 'application/json': ['.json']},
      onDrop: acceptedFiles => {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e && e.target) {
            const text = e.target.result as string;
            setScheduleContent(text);
          }
        }
        setFilename(acceptedFiles[0]['name']);
        reader.readAsText(acceptedFiles[0]);
      }
    });
  
  React.useEffect(() => {
    if (scheduleContent) {
      handleValidate();
    }
  }, [scheduleContent]);

  React.useEffect(() => {

    const getSettingsOperation = get(
      {
        apiName: 'cabb',
        path: '/get_correlator_settings',
        options: {
          queryParams: {
            integration_time: `${ATCAConstants.DEFAULT_INTEGRATION_TIME_S}`
          }
        }
      }
    );

    getSettingsOperation.response
      .then(response => response.body.json())
      .then((response: any) => {        
        if (response['status'] === 'fail') {
          let message = response['message'] || '';
          setSnackMessage({
            severity: 'error',
            message: 'Could not retrieve setting from server: ' + message
          });
        } else {
          setCorrelatorSettings(response);
        }
      })
      .catch((err: Error) => {
        setSnackMessage({
          severity: 'error',
          message: 'Could not retrieve correlator settings from server!'
        });
      });
  }, []);

  React.useEffect(() => {
    setScheduleContent('');
    setValidation([]);
  }, [props.open]);

  const handleDeploy = () => {
    const body = {
      filename: filename,
      content: JSON.parse(scheduleContent)
    }
    const postOperation = post(
      {
        apiName: 'cabb',
        path: '/deploy_schedule_file',
        options: {
          body: body,
          headers: {'content-type': 'application/json'}
        }
      }
    );

    postOperation.response
      .then(response => response.body.json())
      .then((response: any) => {
        if (response['status'] === 'success') {
          setSnackMessage({
            severity: 'info',
            message: 'Schedule file deployed to ATCA.'
          });
        } else {
          setSnackMessage({
            severity: 'error',
            message: 'Could not deploy schedule file: ' + response['message']
          });
        }
      });
  }

  const handleCancel = () => {
    props.handleCancel();
  }

  function handleValidate() {
    const schedule = JSON.parse(scheduleContent);
    const errors = Utils.validateScheduleFile(schedule, correlatorSettings);
    setValidation(errors);
  }

  return (
    <Dialog open={props.open} PaperProps={{ sx: {height: '80%'} }}
      aria-labelledby="validate-schedule-dialog-title"
      onClose={handleCancel}
      maxWidth="md" fullWidth>
      <DialogTitle id="validate-schedule-dialog-title">
        Validate Schedule File
      </DialogTitle>
      <DialogContent sx={{height: '100%'}}>
        <Stack spacing={3} sx={{ height: '100%' }}>
          <Stack direction={'row'} alignItems={'flex-end'}
            justifyContent={'space-between'}>
            <div {...getRootProps({ 
              className: 'dropzone',
              'aria-label': 'drag and drop area a schedule file',
              })}>
              <input {...getInputProps()} />
                <Button>
                  <Typography variant="caption" textAlign='center'>
                    Drag 'n drop ONE json file here, or click to select a file.
                  </Typography>
                </Button>
            </div>
          </Stack>

          <Box
            sx={{ 
              border: '1px solid #bdbdbd',
              borderRadius: '10px',
              padding: '10px',
              height: '100%',
              overflow: 'scroll'
            }}
          >
            <pre style={{width:'100%'}}>
              {scheduleContent}
            </pre>
          </Box>

          <Box
            sx={{ 
              border: '1px solid #bdbdbd',
              borderRadius: '10px',
              padding: '10px',
              height: '100%',
              overflow: 'scroll'
            }}
          >
            <pre style={{width:'100%'}}>
              {validation.length > 0 ? JSON.stringify(validation, null, 4) : "No issues found."}
            </pre>
          </Box>

        </Stack>
      </DialogContent>

      <DialogActions>
        { (validation.length === 0 && Boolean(scheduleContent)) &&
          <Button autoFocus onClick={handleDeploy}>
            Deploy
          </Button>
        }   
        <Button onClick={handleCancel}>
          Close
        </Button>
      </DialogActions>

      <Snackbar
        role = "alert"
        aria-label = {"Alert message"}
        open={Boolean(snackMessage)}
        autoHideDuration={6000}
        onClose={e => setSnackMessage(null)}
      >
        <Alert severity={snackMessage ? snackMessage.severity : 'error' }>
          {snackMessage ? snackMessage.message : ''}
        </Alert>
      </Snackbar>

    </Dialog>
  );
};

